/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ACCESS_REQUEST_BASE_PATH,
  unlockTabPath,
} from "@/constants/access-request";
import { AccessRequestTypeEnum } from "@/enum/access_request";
import UnlockDetail from "@/components/unlock-request/unlock-detail";
import { readAccessDetails } from "@/components/unlock-request/access-details";
import { useGetByIdQuery } from "@/services/request-access";
import { zodResolver } from "@hookform/resolvers/zod";
import { Load } from "components/load";
import PageError from "components/page-error";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { ChevronLeft } from "lucide-react";
import React from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { CardContract } from "./components/card-contract";
import { HistoricoTimeline } from "./components/timeline";
import { UserCard } from "./components/user-card";
import { FormData, FormSchema } from "./validate-form";
import { StatusEnumAccessRequest } from '@/services/dtos/enums/accessRequest.enum';
import { CompletedConfig } from '@/pages/tax/access-request/edit/components/completed-config';
import { CardContract as CardContractCompleted } from '@/pages/tax/access-request/edit/components/card-contract';

export const description =
  "A product edit page. The product edit page has a form to edit the product details, stock, product category, product status, and product images. The product edit page has a sidebar navigation and a main content area. The main content area has a form to edit the product details, stock, product category, product status, and product images. The sidebar navigation has as to product details, stock, product category, product status, and product images.";

export const iframeHeight = "1100px";

export const containerClassName = "w-full h-full";

export const EditRequest: React.FC = () => {
  const navigate = useNavigate();
  const formMethods = useForm<FormData>({
    resolver: zodResolver(FormSchema),
  });
  const { id_request } = useParams();

  const { data, isLoading, error } = useGetByIdQuery(
    id_request?.toString() || ""
  );

  const isClosed = data?.history.some(
    (event: { status: string }) => event.status === StatusEnumAccessRequest.COMPLETED
  );

  const isRejected = data?.history.some(
    (event: { status: string }) => event.status === StatusEnumAccessRequest.REJECTED
  );

  if (isLoading) return <Load />;
  if (error)
    return (
      <PageError
        status={500}
        message="Ocorreu um erro interno. Por favor, tente novamente mais tarde."
      />
    );

  if (data?.type === AccessRequestTypeEnum.UNLOCK) {
    return (
      <UnlockDetail
        request={data}
        backPath={unlockTabPath(ACCESS_REQUEST_BASE_PATH.STORE_BACK_OFFICE)}
        canDecide={false}
      />
    );
  }

  return (
    <>
      <div className="flex flex-col min-h-screen w-full">
        <div className="flex min-h-screen w-full flex-col">
          <div className="flex flex-col sm:gap-4 sm:py-4">
            <main className="grid flex-1 items-start gap-4 p-4 sm:px-6 sm:py-0 md:gap-8">
              <div className="mx-auto grid w-full flex-1 auto-rows-max gap-4">
                <div className="flex items-center gap-4">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() =>
                      navigate("/retaguarda-de-loja/requisicao-de-acesso/")
                    }
                  >
                    <ChevronLeft className="h-4 w-4" />
                    <span className="sr-only">Voltar</span>
                  </Button>
                  <h1 className="flex-1 shrink-0 whitespace-nowrap text-xl font-semibold tracking-tight sm:grow-0">
                    Solicitação de Acesso
                  </h1>
                  <Badge variant="outline" className="ml-auto sm:ml-0">
                    Loja {data?.store}
                  </Badge>

                </div>
                <div className="grid gap-4 md:grid-cols-[1fr_250px] lg:grid-cols-3 lg:gap-8">
                  <div className="grid auto-rows-max items-start gap-4 lg:col-span-2 lg:gap-8">
                    <UserCard
                      formMethods={formMethods}
                      cpf={data?.cpf ?? ""}
                    />
                    {isRejected || (!isClosed ? (
                      <CardContract />
                    ) : (
                      <CompletedConfig accessDetails={readAccessDetails(data?.access_details)} />
                    ))}
                  </div>
                  <div className="grid auto-rows-max items-start gap-4 lg:gap-8">
                    <HistoricoTimeline event={(data as any).history} />
                    {(isClosed || isRejected) && (
                      <CardContractCompleted accessRequestCompleted={(isClosed || isRejected)} />
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-center gap-2 md:hidden">
                  <Button variant="outline" size="sm">
                    Discard
                  </Button>
                  <Button size="sm">Save Product</Button>
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>
    </>
  );
};
